import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { CircleHelp } from "lucide-react";
import { motion } from "framer-motion";
import { Dispatch } from "redux";
import {
	Box,
	IconButton,
	MenuItem,
	Select,
	styled,
	Tooltip,
	tooltipClasses,
	TooltipProps,
	Typography,
} from "@mui/material";

import { getHeaders, URI_API } from "../../utils/config";
import { PageState } from "../../views/Home/HomeUtils";
import { isNurseAdmin } from "../../store/interfaces";
import {
	setTriggerOnboarding,
	setSelectedHospital,
	setSelectedAreaId,
	setSelectedArea,
	AppState,
} from "../../store/reducer";
import {
	AreaItem,
	FloorItem,
	HeaderState,
	HospitalItem,
	labels,
	transition,
	variantsArea,
	variantsName,
} from "./HeaderUtils";

const StyledToolTip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip
		{...props}
		classes={{ popper: className }}
	/>
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: "white",
	},

	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "white",
		boxShadow: "3px 1px 30px -2px rgba(0,0,0,0.24)",
	},
}));

export default function HeaderLeft() {
	const state: HeaderState = useSelector((state: AppState) => state.headerState);
	const page: PageState = useSelector((state: AppState) => state.pageState);
	const currentHospitalId = useSelector((state: AppState) => state.loggedUser).hospital_id;
	const userName: string = useSelector((state: AppState) => state.loggedUser).fullName;
	const isAdmin: boolean = isNurseAdmin(
		useSelector((state: AppState) => state.loggedUser).position! || false,
	);
	const selectedAreaId = useSelector((state: AppState) => state.selectedAreaId);

	const [hospitals, setHospitals] = useState<HospitalItem[]>([]);
	const [selectedHospitalId, setSelectedHospitalId] = useState<string>("");
	const [floors, setFloors] = useState<FloorItem[]>([]);
	const [filteredFloorsIds, setFilteredFloorsIds] = useState<string[]>([]);
	const [areas, setAreas] = useState<AreaItem[]>([]);

	const dispatch: Dispatch<any> = useDispatch();

	const _getAllHospitals = async () => {
		fetch(`${URI_API}/hospitals_per_user`, getHeaders())
			.then((res) => res.json())
			.then((body) => setHospitals(body));
	};

	const _getAllFloors = async () => {
		fetch(`${URI_API}/floors_per_user`, getHeaders())
			.then((res) => res.json())
			.then((body) => setFloors(body));
	};

	const _getAssignedAreas = async () => {
		fetch(`${URI_API}/areas_per_user`, getHeaders())
			.then((res) => res.json())
			.then((body) => setAreas(body));
	};

	useEffect(() => {
		Promise.all([_getAssignedAreas(), _getAllFloors(), _getAllHospitals()]);
		setSelectedHospitalId(currentHospitalId);
	}, []);

	useEffect(() => {
		const filteredFloors = floors.filter(
			(floor: FloorItem) => floor.hospital_id === selectedHospitalId,
		);
		const floorsIds = filteredFloors.map((floor: FloorItem) => floor.id);
		setFilteredFloorsIds(floorsIds);

		const firstArea: AreaItem | undefined = areas
			.filter((area: AreaItem) => floorsIds.includes(area.floor_id))
			.sort((a, b) => a.name.localeCompare(b.name))[0];
		if (firstArea) {
			dispatch(setSelectedAreaId(firstArea.id));
			dispatch(setSelectedArea(firstArea));
			dispatch(
				setSelectedHospital(
					hospitals.find((hospital: HospitalItem) => hospital.id === selectedHospitalId),
				),
			);
		}
	}, [selectedHospitalId, areas, floors]);

	if (state === HeaderState.Normal) {
		return (
			<motion.div
				key={"Label-Name"}
				transition={transition}
				variants={variantsName}
				initial="initial"
				animate="visible"
				exit="exit"
			>
				<Typography className="header__title">Hola, {userName}.</Typography>
				<Typography className="header__subtitle">
					{labels[page]}
					<StyledToolTip
						className="header__tooltip"
						title={"Ver recorrido"}
						placement="top"
						arrow
					>
						<IconButton
							className="header__icon"
							onClick={() => dispatch(setTriggerOnboarding(true))}
						>
							<CircleHelp />
						</IconButton>
					</StyledToolTip>
				</Typography>
			</motion.div>
		);
	}
	if (state === HeaderState.Extended) {
		return (
			<motion.div
				key={"Label-Area"}
				transition={transition}
				variants={variantsArea}
				initial="initial"
				animate="visible"
				exit="exit"
				style={{
					display: "flex",
				}}
			>
				{!isAdmin && (
					<Typography className="header__title">
						{
							hospitals.find(
								(hospital: HospitalItem) => hospital.id === selectedHospitalId,
							)?.name
						}
					</Typography>
				)}
				{isAdmin && (
					<Select
						size="small"
						className="header__select"
						MenuProps={{
							PaperProps: {
								className: "header__select__dropdown",
							},
						}}
						renderValue={(selected: any) => {
							return (
								<Box sx={{ display: "flex", flexDirection: "row" }}>
									<Typography className="header__select__text">
										{
											hospitals.find(
												(hospital: any) => hospital.id === selected,
											)?.name
										}
									</Typography>
								</Box>
							);
						}}
						value={selectedHospitalId}
						onChange={(e: any) => {
							setSelectedHospitalId(e.target.value);
							dispatch(
								setSelectedHospital(
									hospitals.find(
										(hospital: HospitalItem) => hospital.id === e.target.value,
									),
								),
							);
						}}
					>
						{hospitals.map((hospital: HospitalItem) => {
							return (
								<MenuItem
									className="header__select__item"
									value={hospital.id}
									key={hospital.id}
								>
									{hospital.name}
								</MenuItem>
							);
						})}
					</Select>
				)}
				<Select
					size="small"
					className="header__select"
					MenuProps={{
						PaperProps: {
							className: "header__select__dropdown",
						},
					}}
					renderValue={(selected: any) => {
						return (
							<Box sx={{ display: "flex", flexDirection: "row" }}>
								<Typography className="header__select__text">
									{areas.find((area: AreaItem) => area.id === selected)?.name}
								</Typography>
							</Box>
						);
					}}
					value={selectedAreaId}
					onChange={(e: any) => {
						dispatch(setSelectedAreaId(e.target.value));
						dispatch(
							setSelectedArea(
								areas.find((area: AreaItem) => area.id === e.target.value),
							),
						);
					}}
				>
					{areas
						.filter((area: AreaItem) => filteredFloorsIds.includes(area.floor_id))
						.sort((a, b) => a.name.localeCompare(b.name))
						.map((area: AreaItem) => {
							return (
								<MenuItem
									className="header__select__item"
									value={area.id}
									key={area.id}
								>
									{area.name}
								</MenuItem>
							);
						})}
				</Select>
			</motion.div>
		);
	}
	if (state === HeaderState.Collapse) {
		return (
			<motion.div
				key={"Label-Area-Variant"}
				transition={transition}
				variants={variantsArea}
				initial="initial"
				animate="visible"
				exit="exit"
			>
				<Typography className="header__title--variant">
					{areas.find((area: AreaItem) => area.id === selectedAreaId)?.name}
				</Typography>
			</motion.div>
		);
	}

	return null;
}
